import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { type HeroSlide } from '../../data/siteContent';
import type { CmsTextBlock, CmsDividerBlock } from '../../types/websiteCms.types';
import { secureUrl } from '../../utils/secureUrl';

/**
 * Inject a <link rel="preload"> for the first slide image into <head>
 * so the browser discovers the LCP image as early as possible.
 */
function usePreloadLCPImage(imageUrl: string | undefined) {
  const injected = useRef(false);
  useEffect(() => {
    if (!imageUrl || injected.current) return;
    injected.current = true;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = imageUrl;
    link.fetchPriority = 'high';
    document.head.appendChild(link);
    return () => { link.remove(); };
  }, [imageUrl]);
}

// Static class maps (Tailwind purge-safe)
const FONT_SIZE_CLASSES: Record<CmsTextBlock['fontSize'], string> = {
  'xs': 'text-xs', 'sm': 'text-sm', 'base': 'text-base',
  'lg': 'text-lg', 'xl': 'text-xl', '2xl': 'text-2xl',
  '3xl': 'text-3xl', '4xl': 'text-4xl', '5xl': 'text-5xl',
};

const FONT_WEIGHT_CLASSES: Record<CmsTextBlock['fontWeight'], string> = {
  'normal': 'font-normal', 'medium': 'font-medium',
  'semibold': 'font-semibold', 'bold': 'font-bold', 'extrabold': 'font-extrabold',
};

const TEXT_ALIGN_CLASSES: Record<CmsTextBlock['textAlign'], string> = {
  'left': 'text-left', 'center': 'text-center', 'right': 'text-right',
};

const LETTER_SPACING_CLASSES: Record<NonNullable<CmsTextBlock['letterSpacing']>, string> = {
  'normal': 'tracking-normal', 'wide': 'tracking-wide',
  'wider': 'tracking-wider', 'widest': 'tracking-widest',
};

const DIVIDER_WIDTH_MAP: Record<CmsDividerBlock['width'], string> = {
  'narrow': '25%', 'medium': '50%', 'full': '100%',
};

const DIVIDER_THICKNESS_MAP: Record<CmsDividerBlock['thickness'], string> = {
  'thin': '1px', 'normal': '2px', 'thick': '4px',
};

const DIVIDER_ALIGN_MAP: Record<CmsDividerBlock['align'], string> = {
  'left': '0 auto 0 0', 'center': '0 auto', 'right': '0 0 0 auto',
};

const VERTICAL_MAP: Record<string, string> = {
  'top': 'items-start pt-16', 'center': 'items-center', 'bottom': 'items-end pb-16',
};

const HORIZONTAL_MAP: Record<string, string> = {
  'left': 'justify-start pl-12 md:pl-20', 'center': 'justify-center', 'right': 'justify-end pr-12 md:pr-20',
};

const CONTENT_TEXT_ALIGN_MAP: Record<string, string> = {
  'left': 'text-left', 'center': 'text-center', 'right': 'text-right',
};

const CTA_STYLE_CLASSES: Record<string, string> = {
  'primary': 'btn-primary inline-block',
  'outline': 'inline-block px-8 py-3 border-2 border-white text-white rounded-lg font-semibold hover:bg-white/10 transition-colors',
  'ghost': 'inline-block text-white underline font-semibold hover:text-white/80 transition-colors',
};

interface HeroSliderProps {
  slides: HeroSlide[];
  defaultDuration?: number;
}

export default function HeroSlider({ slides, defaultDuration }: HeroSliderProps) {
  const [current, setCurrent] = useState(0);

  // Preload the first slide's image for faster LCP
  usePreloadLCPImage(slides[0]?.bgImage);

  const next = useCallback(() => {
    setCurrent((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prev = useCallback(() => {
    setCurrent((p) => (p - 1 + slides.length) % slides.length);
  }, [slides.length]);

  useEffect(() => {
    if (slides.length <= 1) return;
    const ms = (slides[current].durationSeconds ?? defaultDuration ?? 5) * 1000;
    const timer = setTimeout(next, ms);
    return () => clearTimeout(timer);
  }, [current, next, slides, defaultDuration]);

  if (slides.length === 0) return null;

  return (
    <div className="relative w-full h-[300px] md:h-[500px] overflow-hidden" style={{ containIntrinsicSize: '100vw 500px', contentVisibility: 'visible' }}>
      {slides.map((slide, i) => {
        const hasVideo = !!slide.bgVideo;
        const hasImage = !!slide.bgImage;
        const hasMedia = hasVideo || hasImage;
        const hasBlocks = slide.contentBlocks && slide.contentBlocks.length > 0;

        // Content positioning
        const verticalAlign = VERTICAL_MAP[slide.contentVertical || 'center'];
        const horizontalAlign = HORIZONTAL_MAP[slide.contentPosition || 'center'];
        const contentTextAlign = CONTENT_TEXT_ALIGN_MAP[slide.contentPosition || 'center'];

        // CTA link: development takes priority over ctaLink
        const ctaHref = slide.ctaDevelopmentId
          ? `/property/${slide.ctaDevelopmentSlug || slide.ctaDevelopmentId}`
          : slide.ctaLink;

        const ctaClass = CTA_STYLE_CLASSES[slide.ctaStyle || 'primary'];

        return (
          <div
            key={i}
            className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
              i === current ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
          >
            {/* Background layer: video > image > gradient */}
            {hasVideo ? (
              <video
                src={secureUrl(slide.bgVideo)}
                className="absolute inset-0 w-full h-full object-cover"
                muted
                loop
                autoPlay
                playsInline
              />
            ) : hasImage ? (
              <img
                src={secureUrl(slide.bgImage)}
                alt={slide.title || 'PropInvest hero image'}
                width={1920}
                height={500}
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover"
                loading={i === 0 ? 'eager' : 'lazy'}
                fetchPriority={i === 0 ? 'high' : undefined}
              />
            ) : null}

            {/* Gradient: either overlay on media, or standalone background */}
            <div
              className={`absolute inset-0 ${
                hasMedia
                  ? 'bg-black/40'
                  : `bg-gradient-to-r ${slide.bgColor}`
              }`}
            />

            {/* Content */}
            <div className={`relative h-full flex ${verticalAlign} ${horizontalAlign} z-[1]`}>
              <div className={`${contentTextAlign} px-6 max-w-3xl`}>
                {hasBlocks ? (
                  // Rich content blocks
                  slide.contentBlocks!.map((block, bi) => {
                    if (block.type === 'text') {
                      const sizeClass = FONT_SIZE_CLASSES[block.fontSize];
                      const weightClass = FONT_WEIGHT_CLASSES[block.fontWeight];
                      const alignClass = TEXT_ALIGN_CLASSES[block.textAlign];
                      const spacingClass = LETTER_SPACING_CLASSES[block.letterSpacing || 'normal'];

                      return (
                        <p
                          key={bi}
                          className={`${sizeClass} ${weightClass} ${alignClass} ${spacingClass} mb-2 drop-shadow-lg ${block.italic ? 'italic' : ''} ${block.uppercase ? 'uppercase' : ''}`}
                          style={{ color: block.color }}
                        >
                          {block.text}
                        </p>
                      );
                    }

                    // Divider block
                    return (
                      <hr
                        key={bi}
                        className="border-0 my-3"
                        style={{
                          backgroundColor: block.color,
                          height: DIVIDER_THICKNESS_MAP[block.thickness],
                          width: DIVIDER_WIDTH_MAP[block.width],
                          margin: DIVIDER_ALIGN_MAP[block.align],
                        }}
                      />
                    );
                  })
                ) : (
                  // Legacy fallback: title + subtitle
                  <>
                    <div className={`text-3xl md:text-5xl font-extrabold text-white mb-4 tracking-tight drop-shadow-lg ${i === current ? 'hero-rise hero-rise-1' : ''}`}>
                      {slide.title}
                    </div>
                    {slide.subtitle && (
                      <p className={`text-lg md:text-xl text-white/90 mb-8 drop-shadow ${i === current ? 'hero-rise hero-rise-2' : ''}`}>
                        {slide.subtitle}
                      </p>
                    )}
                  </>
                )}

                {/* CTA button */}
                {slide.ctaText && ctaHref && (
                  ctaHref.startsWith('http') ? (
                    <a
                      href={ctaHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${ctaClass} ${i === current ? 'hero-rise hero-rise-3' : ''}`}
                    >
                      {slide.ctaText}
                    </a>
                  ) : (
                    <Link to={ctaHref} className={`${ctaClass} ${i === current ? 'hero-rise hero-rise-3' : ''}`}>
                      {slide.ctaText}
                    </Link>
                  )
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Proudly South African badge */}
      <img
        src="/images/proudly-sa.png"
        alt="Proudly South African"
        width={64}
        height={64}
        loading="lazy"
        decoding="async"
        className="absolute bottom-3 right-3 z-20 w-14 h-14 md:w-16 md:h-16 drop-shadow-lg"
      />

      {/* Navigation arrows */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prev}
            className="absolute left-3 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/40 text-white rounded-full p-2 transition-colors"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={next}
            className="absolute right-3 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/40 text-white rounded-full p-2 transition-colors"
            aria-label="Next slide"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* Dots */}
      {slides.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className="p-2"
              aria-label={`Go to slide ${i + 1}`}
            >
              <span className={`block w-3 h-3 rounded-full transition-colors ${
                i === current ? 'bg-white' : 'bg-white/40'
              }`} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
